import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IResponse } from 'src/app/pages/base/interfaces';
import { MaestroModel } from '../maestro.model';
import { UsuarioService } from '../../services/seguridad/usuario.service';


export class GrupoAcceso extends MaestroModel {

    public static RECORD_NAME = 'grupos_acceso';
    public static CODIGO_REGISTRO = 'grupo_acceso';
    public static CODIGO_MODULO = 'seguridad';
    public static NOMBRE_SINGULAR = 'Grupo de Acceso';
    public static NOMBRE_PLURAL = 'Grupos de Acceso';

    private _accesoTotal: boolean;
    private _modulos: GrupoAccesoModulo[] | Record<string, any>[];
    private _registros: GrupoAccesoRegistro[] | Record<string, any>[];
    private _reportes: GrupoAccesoReporte[] | Record<string, any>[];
    private _permisos: GrupoAccesoPermiso[] | Record<string, any>[];

    public static ACTUALIZAR_SALDO_GRUPO = 'actualizar_saldo_grupo';
    public static ACTUALIZAR_SALDO_TARJETA = 'actualizar_saldo_tarjeta';
    public static ENVIAR_EXTRACTO = 'enviar_extracto';
    public static PRESTAMO = 'prestamo';
    public static LISTADO_RENDICION_CAJA = 'listado_rendicion_caja';
    public static LISTADO_RENDICION = 'listado_rendicion';
    public static RENDICION = 'rendicion';
    public static MENU_RENDICION_CAJA = 'menu_rendicion_caja';
    public static ADMINISTRADOR = 'administrador';




    constructor() {
        super();
        this._accesoTotal = true;
        this._modulos = [];
        this._registros = [];
        this._reportes = [];
        this._permisos = [];
    }

    public set accesoTotal( accesoTotal: boolean ) {
        this._accesoTotal = accesoTotal;
    }
    public get accesoTotal(): boolean {
        return this._accesoTotal;
    }

    public set modulos( modulos: Record<string, any>[] ) {
        this._modulos = modulos;
    }
    public get modulos() {
        return this._modulos;
    }

    public set permisos( permisos: Record<string, any>[] ) {
        this._permisos = permisos;
    }
    public get permisos() {
        return this._permisos;
    }

    public set registros( registros: Record<string, any>[] ) {
        this._registros = registros;
    }
    public get registros() {
        return this._registros;
    }

    public set reportes( reportes: Record<string, any>[] ) {
        this._reportes = reportes;
    }
    public get reportes() {
        return this._reportes;
    }

    public set serverJson( objeto: Record<string, any> ) {
        for (const registro of objeto.registros) {
            if ( typeof registro.sucursales !== 'string' ) {
                const strSuc = registro.sucursales?.join(",");
                registro.sucursales = strSuc;
            }
        }
        super.serverJson = objeto;
    }
    public get serverJson(): Record<string, any> {
        return super.serverJson;
    }

    public static get columsOnLista(): Record<string, any>[] {
        return [
            { field: 'id', label: 'ID', type: 'id' },
            { field: 'nombre', label: 'Nombre', type: 'text' },
            { field: 'acceso_total', label: 'Acceso Total', type: 'boolean' },
        ]
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        await super.cargarObjeto( objeto );
        this._accesoTotal = objeto.acceso_total;
        // verificamos exista un modulo nuevo para agregar
        for (const modulo of this._modulos) {
            if ( objeto.modulos?.findIndex( m => m.codigo === modulo.codigo ) === -1 ) {
                objeto.modulos?.push( modulo );
            }
        }
        this._modulos = objeto.modulos;

        for (const registro of this._registros) {
            if ( objeto.registros?.findIndex( r => `${r.modulo}_${r.codigo}` === `${registro.modulo}_${registro.codigo}` ) === -1 ) {
                objeto.registros?.push( registro );
            }
        }
        this._registros = objeto.registros;

        for (const reporte of this._reportes) {
            if ( objeto.reportes?.findIndex( r => r.codigo === reporte.codigo ) === -1 ) {
                objeto.reportes?.push( reporte );
            }
        }
        this._reportes = objeto.reportes;

        for (const permiso of this._permisos) {
            if ( objeto.permisos?.findIndex( r => r.codigo === permiso.codigo ) === -1 ) {
                objeto.permisos?.push( permiso );
            }
        }
        this._permisos = objeto.permisos;
    }

    /**
     * Retorna el formulario a utilizar en las ventanas
     * @returns FormGroup
     */
    async getFormGroup(): Promise<FormGroup> {
        let fg = await super.getFormGroup();
        fg.addControl("acceso_total", new FormControl(this._accesoTotal) );
        fg.addControl("modulos", await this.cargarModulos() );
        fg.addControl("permisos", await this.cargarPermisos() );
        fg.addControl("registros", await this.cargarRegistros() );
        fg.addControl("reportes", await this.cargarReportes() );
        return fg;
    }

    async cargarModulos() {
        const modulos = new FormArray([]);
        for (const modulo of this._modulos) {
            const obj = new GrupoAccesoModulo();
            await obj.cargarObjeto(modulo);
            modulos.push( await obj.getFormGroup() );
        }
        return modulos;
    }

    async cargarPermisos() {
        const permisos = new FormArray([]);
        for (const permiso of this._permisos) {
            const obj = new GrupoAccesoPermiso();
            await obj.cargarObjeto(permiso);
            permisos.push( await obj.getFormGroup() );
        }
        return permisos;
    }

    async cargarReportes() {
        const reportes = new FormArray([]);
        for (const reporte of this._reportes) {
            const obj = new GrupoAccesoReporte();
            await obj.cargarObjeto(reporte);
            reportes.push( await obj.getFormGroup() );
        }
        return reportes;
    }

    async cargarRegistros() {
        const registros = new FormArray([]);
        for (const registro of this._registros) {
            const obj = new GrupoAccesoRegistro();
            await obj.cargarObjeto(registro);
            registros.push( await obj.getFormGroup() );
        }
        return registros;
    }

    async actualizarDatos( objeto: Record< string, any> ) {
        await super.actualizarDatos( objeto );
        this._accesoTotal = objeto.acceso_total;
        this._modulos = objeto.modulos;
        this._registros = objeto.registros;
        this._reportes = objeto.reportes;
        this._permisos = objeto.permisos;
    }

    async guardar( service: UsuarioService ): Promise<IResponse> {
        return await service.post( this.serverJson );
    }

    async actualizar( service: UsuarioService  ): Promise<IResponse> {
        return await service.put(this.id, this.serverJson);
    }

    async eliminar( service: UsuarioService ): Promise<IResponse> {
        return await service.delete(this.id);
    }

}

export class GrupoAccesoModulo {

    private _id: number;
    private _idGrupoAcceso: number;
    private _codigo: string;
    private _nombre: string;
    private _accesoTotal: boolean;
    private _activo: boolean;

    constructor() {
        this._activo = false;
    }

    public get codigo() {
        return this._codigo;
    }

    public get activo() {
        return this._activo;
    }

    public get accesoTotal() {
        return this._accesoTotal;
    }

    async getFormGroup(): Promise<FormGroup> {
        const fb = new FormBuilder();
        const form = fb.group({
            id:                     [this._id],
            id_grupo_acceso:        [this._idGrupoAcceso],
            codigo:                 [this._codigo],
            nombre:                 [this._nombre],
            acceso_total:           [this._accesoTotal],
            activo:                 [this._activo]
        });

        return form;
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        this._id = objeto?.id
        this._idGrupoAcceso = objeto?.id_grupo_acceso;
        this._codigo = objeto?.codigo;
        this._nombre = objeto?.nombre;
        this._activo = objeto?.activo;
        this._accesoTotal = objeto?.acceso_total;
    }

}

export class GrupoAccesoRegistro {

    public static TIPO_MAESTRO = 1;
    public static TIPO_TRANSACCION = 2;

    public static ACCESO_TODO = 1;
    public static ACCESO_SUCURSAL = 2;
    public static ACCESO_USUARIO = 3;
    

    public static TIPOS = [
        { valor: 1, nombre: 'Maestro' },
        { valor: 2, nombre: 'Transacción' },
    ]

    public static ACCESOS = [
        { valor: 1, nombre: 'Todo' },
        { valor: 2, nombre: 'Sucursal' },
        { valor: 3, nombre: 'Usuario' },
    ]

    private _id: number;
    private _idGrupoAcceso: number;
    private _modulo: string;
    private _codigo: string;
    private _nombre: string;
    private _tipo: number;
    private _acceso: number;
    private _ver: boolean;
    private _crear: boolean;
    private _editar: boolean;
    private _eliminar: boolean;
    private _anular: boolean;
    private _sucursales: number[];
    private _importar: boolean;
    private _exportar: boolean;
    private _inactivar: boolean;
    private _aprobar: boolean;
    private _revertirAprobacion: boolean;
    private _revertirAnulacion: boolean;
    private _revertirInactivacion: boolean;
    private _imprimir: boolean;


    constructor() {
        this._tipo = 1;
        this._acceso = 1;
        this._ver = false
        this._crear = false;
        this._editar = false;
        this._eliminar = false;
        this._anular = false;
        this._importar = false;
        this._exportar = false;
        this._aprobar = false;
        this._inactivar = false;
        this._revertirAnulacion = false;
        this._revertirAprobacion = false;
        this._revertirInactivacion = false;
        this._imprimir = false;
    }

    public set modulo( modulo: string ) {
        this._modulo = modulo;
    }
    public get modulo(): string {
        return this._modulo;
    }
    
    public set codigo( codigo: string ) {
        this._codigo = codigo;
    }
    public get codigo() {
        return this._codigo;
    }

    public set nombre( nombre: string ) {
        this._nombre = nombre;
    }
    public get nombre() {
        return this._nombre;
    }

    public set tipo( tipo: number ) {
        this._tipo = tipo;
    }
    public get tipo() {
        return this._tipo;
    }

    public set acceso( acceso: number ) {
        this._acceso = acceso;
    }
    public get acceso() {
        return this._acceso;
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        this._id = objeto.id
        this._idGrupoAcceso = objeto.id_grupo_acceso
        this._modulo = objeto.modulo
        this._codigo = objeto.codigo
        this._nombre = objeto.nombre
        this._tipo = objeto.tipo
        this._acceso = objeto.acceso
        this._crear = objeto.crear
        this._editar = objeto.editar
        this._eliminar = objeto.eliminar
        this._anular = objeto.anular
        this._ver = objeto.ver
        this._importar = objeto.importar
        this._exportar = objeto.exportar
        this._inactivar = objeto.inactivar
        this._aprobar = objeto.aprobar
        this._revertirAnulacion = objeto.revertir_anulacion
        this._revertirAprobacion = objeto.revertir_aprobacion
        this._revertirInactivacion = objeto.revertir_inactivacion
        this._imprimir = objeto.imprimir
        this._sucursales = objeto.sucursales?.split(",").map( x => +x)
    }

    async getFormGroup(): Promise<FormGroup> {
        const fb = new FormBuilder();
        const form = fb.group({
            id:                     [this._id],
            id_grupo_acceso:        [this._idGrupoAcceso],
            modulo:                 [this._modulo],
            codigo:                 [this._codigo],
            nombre:                 [this._nombre],
            tipo:                   [this._tipo],
            acceso:                 [this._acceso],
            crear:                  [this._crear],
            editar:                 [this._editar],
            eliminar:               [this._eliminar],
            anular:                 [this._anular],
            ver:                    [this._ver],
            importar:               [this._importar],
            exportar:               [this._exportar],
            inactivar:              [this._inactivar],
            aprobar:                [this._aprobar],
            revertir_aprobacion:    [this._revertirAprobacion],
            revertir_anulacion:     [this._revertirAnulacion],
            revertir_inactivacion:  [this._revertirInactivacion],
            imprimir:               [this._imprimir],
            sucursales:             [this._sucursales]
        });

        return form;
    }

}

export class GrupoAccesoReporte {

    private _id: number;
    private _idGrupoAcceso: number;
    private _modulo: string;
    private _codigo: string;
    private _nombre: string;
    private _activo: boolean;

    constructor() {
        this._activo = false;
    }
    
    public set modulo( modulo: string ) {
        this._modulo = modulo;
    }
    public get modulo() {
        return this._modulo;
    }

    public set codigo( codigo: string ) {
        this._codigo = codigo;
    }
    public get codigo() {
        return this._codigo;
    }

    public set nombre( nombre: string ) {
        this._nombre = nombre;
    }
    public get nombre() {
        return this._nombre;
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        this._id = objeto?.id
        this._idGrupoAcceso = objeto?.id_grupo_acceso;
        this._modulo = objeto?.modulo;
        this._codigo = objeto?.codigo;
        this._nombre = objeto?.nombre;
        this._activo = objeto?.activo;
    }

    async getFormGroup(): Promise<FormGroup> {
        const fb = new FormBuilder();
        const form = fb.group({
            id:                     [this._id],
            id_grupo_acceso:        [this._idGrupoAcceso],
            modulo:                 [this._modulo],
            codigo:                 [this._codigo],
            nombre:                 [this._nombre],
            activo:                 [this._activo]
        });

        return form;
    }

}

export class GrupoAccesoPermiso {
    private _id: number;
    private _idGrupoAcceso: number;
    private _codigo: string;
    private _nombre: string;
    private _activo: boolean;

    constructor() {
        this._activo = false;
    }

    public get codigo() {
        return this._codigo;
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        this._id = objeto?.id
        this._idGrupoAcceso = objeto?.id_grupo_acceso;
        this._codigo = objeto?.codigo;
        this._nombre = objeto?.nombre;
        this._activo = objeto?.activo;
    }

    async getFormGroup(): Promise<FormGroup> {
        const fb = new FormBuilder();
        const form = fb.group({
            id:                     [this._id],
            id_grupo_acceso:        [this._idGrupoAcceso],
            codigo:                 [this._codigo],
            nombre:                 [this._nombre],
            activo:                 [this._activo]
        });

        return form;
    }
    
}
